import {
	Card,
	CardActions,
	CardBody,
	CardTitle,
} from "@tql/ui-components-react/kendo/layout";
import type { KeyboardEvent } from "react";

import type { InvoiceFilter } from "~/shared/types/payment-types.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { SummaryCountIndicator } from "../summary-count-indicator/index.tsx";

type SummaryCardValue = {
	amount: number;
	count?: number;
};

type SummaryCardProps = {
	key: string;
	filter: InvoiceFilter;
	title: string;
	value: SummaryCardValue & { icon: React.ReactNode };
	handleFilterChange: (filter: InvoiceFilter) => void;
	isSelected?: boolean;
};

const camelCaseToWords = (s: string) => {
	const result = s
		.replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replaceAll(/([A-Z])([A-Z][a-z])/g, "$1 $2");
	return result.charAt(0).toUpperCase() + result.slice(1);
};

export const SummaryCard = ({
	filter,
	title,
	value,
	handleFilterChange,
	isSelected = false,
}: SummaryCardProps) => {
	const handleClick = () => {
		handleFilterChange(filter);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			handleClick();
		}
	};

	// Matches the alert badge's own visibility rules so the hue and the badge agree.
	const hasAlerts = filter !== "outstandingBalance" && (value.count ?? 0) > 0;
	const hueClass = hasAlerts
		? "invoice-summary-card-selected-alert"
		: "invoice-summary-card-selected-plain";
	const selectedClass = isSelected
		? ` invoice-summary-card-selected ${hueClass}`
		: "";

	return (
		<div
			aria-pressed={isSelected}
			className={`invoice-summary-card${selectedClass}`}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			role={"button"}
			tabIndex={0}
		>
			<div className={"invoice-summary-card-inner"}>
				<div className={"invoice-summary-card-icon"}>{value.icon}</div>
				<Card className={"invoice-summary-card-content"}>
					<CardBody>
						<CardTitle className={"invoice-summary-card-title"}>
							<div>{camelCaseToWords(title)}</div>
							<SummaryCountIndicator
								amount={value.amount}
								count={value.count}
								showAsCurrency={filter === "outstandingBalance"}
							/>
						</CardTitle>

						<p style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
							{formatCurrency(value.amount)}
						</p>
					</CardBody>
					<CardActions></CardActions>
				</Card>
			</div>
		</div>
	);
};
